/**
 * Per-roster-entry detail shapes — served by the unified per-entry
 * endpoints (GET /api/v1/roster/{entry_id}/{script,content,threads/...}).
 *
 * Mirror the BE structs in internal/common/responses/roster_entry.go
 * (ScriptDetail / ContentDetail) and internal/threads/dto.go
 * (MessageView / GetMessagesResponse).
 */

import type { GeoAiEnvelope, ScriptReviewAiEnvelope } from './script-review';
import type { AssignedProduct } from './roster';
import type { MessageAttachment } from './thread-files';

/** Generic AI-envelope shape used by Content review (status is one of
 *  the three lifecycle values; `data` is the raw AI service blob —
 *  shape varies per analysis type and the FE component picks what it
 *  needs). Keep loose until product converges on a stable schema. */
export interface ContentReviewAiEnvelope {
  status: 'pending' | 'complete' | 'failed';
  analysis_id?: string;
  data?: Record<string, unknown>;
  error?: string;
}

/**
 * Full Script detail — body + AI envelope. Returned by
 * GET /api/v1/roster/{entry_id}/script. `text` / `pdf_url` are
 * non-empty when admin has uploaded a script version; absent on
 * script_pending rows. `ai_analysis` is omitted when no script
 * version exists yet.
 */
/** A script's native document: the editor's serialized Lexical
 *  SerializedEditorState, stored and rendered verbatim. Kept opaque here (a
 *  JSON object) so this shared package stays free of any editor dependency —
 *  the UI casts it to Lexical's type at the render boundary. */
export type ScriptDoc = Record<string, unknown>;

export interface ScriptDetail {
  entry_id: string;
  campaign_id: string;
  status: string;
  /** Roster post workflow status (e.g. `script_review`, `script_approved`) —
   *  what ScriptsTab's `dc.status` gates its Approve button on. Distinct from
   *  `status` above, which is only ever `pending`/`approved` on the script
   *  sub-document itself and never equals the workflow phase name. */
  post_status?: string;
  version: number;
  version_id: string;
  pdf_url?: string;
  /** The script's native document tree (Lexical). Rendered verbatim by the
   *  read-only ScriptDocRenderer. Every script is doc-JSON; there is no
   *  markdown/plain-text fallback. */
  doc?: ScriptDoc;
  uploaded_by?: string;
  uploaded_at?: string;
  ai_analysis?: ScriptReviewAiEnvelope;
  /** GEO optimizer pack envelope (YouTube only). Omitted until generated. */
  geo_analysis?: GeoAiEnvelope;
  /** Products assigned to this creator (Multi-Product Campaigns), resolved by
   *  id against the campaign catalog. Drives the read-only "Featuring" chips
   *  in the script-review header. Omitted when the creator has no assignment. */
  assigned_products?: AssignedProduct[];
}

/**
 * Full Content detail — video URL + AI envelope. Returned by
 * GET /api/v1/roster/{entry_id}/content. `url` is the
 * content-version URL; `ai_analysis` is omitted when no content
 * version exists yet.
 */
export interface ContentDetail {
  entry_id: string;
  campaign_id: string;
  status: string;
  /** Roster post workflow status (e.g. `content_review`, `content_approved`) —
   *  what ContentTab's `dc.status` gates its Approve button on. Distinct from
   *  `status` above, which is only ever `pending`/`approved` on the content
   *  sub-document itself and never equals the workflow phase name. */
  post_status?: string;
  version: number;
  version_id: string;
  url: string;
  /** Presigned S3 GET URL for the AI-normalised MP4 (Drive submissions only).
   *  Only present once AI analysis is complete. FE shows loading spinner until
   *  this field appears; never renders the raw Drive link in the video player. */
  video_s3_url?: string;
  deliverable_type?: string;
  title?: string;
  description?: string;
  comment?: string;
  uploaded_by?: string;
  uploaded_at?: string;
  ai_analysis?: ContentReviewAiEnvelope;
  /** GEO finalize pack envelope (YouTube only) — chapter timestamps +
   *  publish-ready description stitched onto the uploaded video's transcript.
   *  Omitted until generated. Mirrors ScriptDetail.geo_analysis's envelope
   *  shape (GeoAiEnvelope), different field name per the BE's content-review
   *  DTO (internal/common/responses/roster_entry.go). */
  geo_finalize?: GeoAiEnvelope;
  /** Products assigned to this creator (Multi-Product Campaigns), resolved by
   *  id against the campaign catalog. Drives the read-only "Featuring" chip and
   *  the product-match verdict in content review. Omitted when none assigned. */
  assigned_products?: AssignedProduct[];
}

/** Mirrors Go's UploadContentRequest. */
export interface ContentSubmission {
  url?: string;
}

/**
 * Quote-reply anchor tying a message to a span of script text — mirrors
 * BE's `AnchorPayload` (internal/threads/dto.go). Presence of an anchor
 * is what makes a message an annotation; `kind` is not involved.
 */
export interface TextMessageAnchor {
  /** Omitted or "text" — the script-quote variant. */
  type?: 'text';
  /** Exact script text the comment refers to, verbatim and untruncated. */
  quoted_text: string;
  /** Issue tag (AI aspect taxonomy or team-only tags). Omitted = untagged. */
  aspect?: string;
  /** Which gesture produced the anchor. */
  origin: 'row' | 'selection';
  /** Script revision the anchor was made against (hex ObjectID). */
  script_version_id?: string;
  /** 0-based Nth match of quoted_text in the rendered script. */
  occurrence?: number;
}

export type AnchorShapeKind = 'box' | 'arrow' | 'pen';

// WIRE CONTRACT — the single-letter field names below are the backend's, kept
// short because one note can carry many shapes; do not rename them.
/** One markup stroke on a captured frame, in 0..1 fractions of the frame. */
export interface AnchorShape {
  k: AnchorShapeKind;
  x1?: number;
  y1?: number;
  x2?: number;
  y2?: number;
  /** Pen strokes only. */
  pts?: [number, number][];
  /** Palette colour, e.g. "#DC2626". */
  c: string;
}

/** The captured, baked image behind a media note — uploaded like any other
 *  chat file (POST .../threads/content/files) and referenced here by id,
 *  the same way `MessageView.attachments[].file_id` works. */
export interface AnchorFrame {
  file_id: string;
  width: number;
  height: number;
  shapes?: AnchorShape[];
}

/**
 * Media anchor — a moment on the content video, optionally with a captured
 * frame. Mirrors the media variant of BE's `AnchorPayload`.
 */
export interface MediaMessageAnchor {
  type: 'media';
  /** Content version the note was made on (hex ObjectID). */
  content_version_id: string;
  /** Playhead position in milliseconds. */
  t_ms: number;
  frame?: AnchorFrame;
}

/** Either anchor variant. Narrow with `isMediaAnchor` / `isTextAnchor`. */
export type MessageAnchor = TextMessageAnchor | MediaMessageAnchor;

const MEDIA_ANCHOR_TYPE: MediaMessageAnchor['type'] = 'media';

/** True for the media (video moment) anchor variant. */
export function isMediaAnchor(anchor: MessageAnchor | undefined): anchor is MediaMessageAnchor {
  return anchor?.type === MEDIA_ANCHOR_TYPE;
}

/** True for the script-quote anchor variant (type omitted or "text"). */
export function isTextAnchor(anchor: MessageAnchor | undefined): anchor is TextMessageAnchor {
  return !!anchor && anchor.type !== MEDIA_ANCHOR_TYPE;
}

/** POST /roster/{entry_id}/threads/content/messages — note variant. */
export interface PostNoteRequest {
  body: string;
  anchor?: MediaMessageAnchor;
  reply_to?: string;
  /** UUID; a retried POST with the same id returns the original message. */
  client_id: string;
  /** Ids of files already uploaded via POST .../threads/content/files,
   *  attached to this message. Omitted/empty when the message carries no
   *  files (the common case). */
  file_ids?: string[];
}

/** One row in the script version archive listing — mirrors BE's
 *  ScriptVersionSummary. Newest first; first row is the live version. */
export interface ScriptVersionSummary {
  id: string;
  version: number;
  uploaded_by?: string;
  uploaded_at?: string;
  current?: boolean;
}

export interface ScriptVersionListing {
  versions: ScriptVersionSummary[];
}

/** A stored script revision with its markdown body — the read-only
 *  archive view (mirrors BE's ScriptVersionDetail). */
export interface ScriptVersionDetail {
  id: string;
  version: number;
  text?: string;
  pdf_url?: string;
  uploaded_by?: string;
  uploaded_at?: string;
}

/**
 * Client-facing single thread message — mirrors BE's `MessageView`
 * exactly. `kind` is empty / omitted for regular chat bubbles;
 * non-empty values (e.g. `"rejection"`) surface as decision-event
 * rows with distinct rendering.
 */
export interface MessageView {
  id: string;
  thread_id: string;
  sender_id: string;
  sender_role: string;
  sender_name: string;
  sender_title: string;
  body: string;
  /** Empty / omitted for regular messages. Decision-event variants
   *  (rejection, approval, etc.) carry a kind string the FE branches on. */
  kind?: string;
  /** Present on quote-reply messages tied to script text. */
  anchor?: MessageAnchor;
  /** Present on nested replies — the parent message id (same thread). */
  reply_to?: string;
  /** Echo of the idempotency key the sender supplied, if any. */
  client_id?: string;
  /** Sender's profile photo URL (when the BE can resolve one). */
  sender_avatar?: string;
  /** True once the author has edited the body — the FE shows an "edited"
   *  marker. Omitted (false) for untouched messages. */
  edited?: boolean;
  created_at: string;
  /** Server-computed permission for THIS viewer on THIS message — the sole
   *  source of truth for the edit/delete affordances (seen-state + role +
   *  24h ceiling matrix; see discovr's EvaluateMessagePermissions). The FE
   *  must not re-derive these from role/author checks. Omitted on BE
   *  responses that predate this field — treated as false (hide the
   *  affordance) rather than assumed true. */
  viewer_can_edit?: boolean;
  viewer_can_delete?: boolean;
  /** Files attached to this message. Omitted/empty on messages
   *  with no attachments — the common case, and every message predating
   *  this field. */
  attachments?: MessageAttachment[];
}

/**
 * Cursor-paginated thread page — mirrors BE's `GetMessagesResponse`.
 * `next_cursor` is empty when there is no next page.
 */
export interface ThreadMessagesPage {
  messages: MessageView[];
  next_cursor?: string;
  /** The calling user's own id — lets the UI identify the viewer's own
   *  messages (for the Edit affordance) by identity, not role side.
   *
   *  Stays optional deliberately, for the same reason as `locked` below: the
   *  current BE populates it on every thread-messages response, but a response
   *  from a BE predating the field omits it. Consumers must treat an absent
   *  `viewer_id` as "viewer unknown" and hide the author-only affordances
   *  rather than assume it is present. */
  viewer_id?: string;
  /** True when the thread is read-only: entry status for selection threads,
   *  review-approved for script/content threads. Authoritative once the
   *  thread-messages fetch has loaded — supersedes the roster row's own
   *  `*_thread_locked` field (a fetch-time hint used before this response
   *  is available). Omitted on older BE responses that predate this field. */
  locked?: boolean;
}
